export interface PlaytestStateSample {
  readonly atMs: number;
  readonly state: unknown;
}

export interface PlaytestEventSample {
  readonly atMs: number;
  readonly name: string;
  readonly detail: unknown;
}

export interface PlaytestVisualEvidence {
  readonly dataUrl?: string;
  readonly text: string;
  readonly error?: string;
}

export type TestInputV2 =
  | { readonly type: "pointer"; readonly phase: "down" | "move" | "up" | "cancel"; readonly pointerId: number; readonly x: number; readonly y: number }
  | { readonly type: "key"; readonly phase: "down" | "up"; readonly key: string; readonly code: string };

export interface InputControl {
  readonly bindingId: number;
  readonly action: string;
  readonly kind: "button" | "horizontal-axis";
  readonly rect: { readonly x: number; readonly y: number; readonly width: number; readonly height: number };
}
export interface InputReceipt {
  readonly sequence: number;
  readonly action: string | null;
  readonly phase: string;
  readonly receivedAtMs: number;
  readonly synthetic: boolean;
}
export interface PlaytestEvidence {
  readonly version: 1;
  readonly controls?: readonly InputControl[];
  readonly inputReceipts?: readonly InputReceipt[];
  readonly inputActions?: readonly { readonly intendedAtMs: number; readonly sentAtMs: number; readonly action: TestInputV2 }[];
  readonly ready: boolean;
  readonly durationMs: number;
  readonly actionsSent: number;
  readonly stateChanges: number;
  readonly states: readonly PlaytestStateSample[];
  readonly events: readonly PlaytestEventSample[];
  readonly errors: readonly string[];
  readonly visual?: PlaytestVisualEvidence;
}

export interface PlaytestMessageEvent {
  readonly data: unknown;
}

export interface PlaytestTransport {
  subscribe(listener: (event: PlaytestMessageEvent) => void): () => void;
  post(message: unknown): void;
}

export interface ExplorationAction {
  readonly delayMs: number;
  readonly action:
    | { readonly type: "pointer"; readonly x: number; readonly y: number }
    | { readonly type: "key"; readonly key: string }
    | TestInputV2;
}

export interface PlaytestOptions {
  readonly signal?: AbortSignal;
  readonly actions?: readonly ExplorationAction[];
  readonly settleMs?: number;
  readonly readyTimeoutMs?: number;
  readonly sleep?: (milliseconds: number) => Promise<void>;
}

const DEFAULT_ACTIONS: readonly ExplorationAction[] = [
  { delayMs: 120, action: { type: "pointer", x: 0.5, y: 0.5 } },
  { delayMs: 120, action: { type: "pointer", x: 0.22, y: 0.72 } },
  { delayMs: 120, action: { type: "pointer", x: 0.78, y: 0.72 } },
  { delayMs: 120, action: { type: "pointer", x: 0.22, y: 0.28 } },
  { delayMs: 120, action: { type: "pointer", x: 0.78, y: 0.28 } },
  { delayMs: 100, action: { type: "key", key: "ArrowUp" } },
  { delayMs: 100, action: { type: "key", key: "ArrowRight" } },
  { delayMs: 100, action: { type: "key", key: " " } },
  { delayMs: 100, action: { type: "key", key: "Enter" } },
];

function discoveredActions(controls: readonly InputControl[]): readonly ExplorationAction[] {
  const movement = controls.find(c => c.kind === "horizontal-axis");
  const jump = controls.find(c => c.kind === "button" && c.action === "jump") ?? controls.find(c => c.kind === "button");
  if (!movement || !jump) return DEFAULT_ACTIONS;
  const move = { type: "pointer" as const, pointerId: 1, x: movement.rect.x + movement.rect.width * 0.9, y: movement.rect.y + movement.rect.height / 2 };
  const button = { type: "pointer" as const, pointerId: 2, x: jump.rect.x + jump.rect.width / 2, y: jump.rect.y + jump.rect.height / 2 };
  return [
    { delayMs: 0, action: { ...move, phase: "down" } },
    { delayMs: 240, action: { ...button, phase: "down" } },
    { delayMs: 80, action: { ...button, phase: "up" } },
    { delayMs: 280, action: { ...button, phase: "down" } },
    { delayMs: 200, action: { ...button, phase: "up" } },
    { delayMs: 200, action: { ...move, phase: "up" } },
  ];
}

function inputControls(value: unknown): InputControl[] {
  if (!Array.isArray(value)) return [];
  return value.slice(0, 32).flatMap((c: unknown) => {
    if (!c || typeof c !== "object") return [];
    const control = c as InputControl;
    const r = control.rect;
    if (!Number.isInteger(control.bindingId) || typeof control.action !== "string" || !/^[a-z][a-zA-Z0-9_-]{0,39}$/.test(control.action)
      || !["button", "horizontal-axis"].includes(control.kind) || !r || ![r.x,r.y,r.width,r.height].every(Number.isFinite)
      || r.width <= 0 || r.height <= 0 || r.x < 0 || r.y < 0 || r.x + r.width > 1.001 || r.y + r.height > 1.001) return [];
    return [{bindingId:control.bindingId,action:control.action,kind:control.kind,rect:{x:r.x,y:r.y,width:r.width,height:r.height}}];
  });
}

function now(): number { return Date.now(); }
function sleep(milliseconds: number): Promise<void> { return new Promise((resolve) => setTimeout(resolve, milliseconds)); }

function safeText(value: unknown, max = 1_200): string {
  if (typeof value === "string") return value.slice(0, max);
  try { return JSON.stringify(value).slice(0, max); }
  catch { return String(value).slice(0, max); }
}

function stableStateKey(value: unknown): string {
  return safeText(value, 8_000);
}

export async function collectExploratoryPlaytest(
  transport: PlaytestTransport,
  options: PlaytestOptions = {},
): Promise<PlaytestEvidence> {
  const startedAt = now();
  const states: PlaytestStateSample[] = [];
  const events: PlaytestEventSample[] = [];
  const errors: string[] = [];
  let controls: InputControl[] = [];
  const inputReceipts: InputReceipt[] = [];
  const inputActions: { intendedAtMs: number; sentAtMs: number; action: TestInputV2 }[] = [];
  const active = new Map<string, TestInputV2>();
  let visual: PlaytestVisualEvidence | undefined;
  let ready = false;
  let actionsSent = 0;
  let lastStateKey: string | null = null;
  let stateChanges = 0;
  let resolveReady: (() => void) | undefined;
  const readySignal = new Promise<void>((resolve) => { resolveReady = resolve; });

  const unsubscribe = transport.subscribe((event) => {
    const data = event.data;
    if (!data || typeof data !== "object") return;
    const message = data as Record<string, unknown>;
    const atMs = Math.max(0, now() - startedAt);
    if (message.type === "carcade-controls-v1") controls = inputControls(message.controls);
    if (message.type === "carcade-input-receipt-v1" && inputReceipts.length < 32
      && Number.isInteger(message.sequence) && Number.isFinite(message.receivedAtMs)
      && (message.action === null || typeof message.action === "string")
      && ["down", "move", "up", "cancel"].includes(String(message.phase)) && typeof message.synthetic === "boolean") {
      inputReceipts.push({ sequence: message.sequence as number, action: typeof message.action === "string" ? message.action.slice(0,40) : null,
        phase: message.phase as string, receivedAtMs: message.receivedAtMs as number, synthetic: message.synthetic });
    }
    if (message.type === "carcade-ready-v1") { ready = true; resolveReady?.(); }
    if (message.type === "carcade-state-v1") {
      const key = stableStateKey(message.state);
      if (key !== lastStateKey) {
        if (lastStateKey !== null) stateChanges += 1;
        lastStateKey = key;
        const sample = { atMs, state: message.state };
        const last = states[states.length - 1];
        if (states.length > 1 && last && Math.floor(last.atMs / 100) === Math.floor(atMs / 100)) states[states.length - 1] = sample;
        else { if (states.length === 16) states.splice(1, 1); states.push(sample); }
      }
    }
    if (message.type === "carcade-event-v1" && typeof message.name === "string" && events.length < 24) {
      events.push({ atMs, name: message.name, detail: message.detail });
    }
    if (message.type === "carcade-error-v1" && errors.length < 24) errors.push(safeText(message.message, 600));
    if (message.type === "carcade-visual-v1") {
      visual = {
        ...(typeof message.dataUrl === "string" && message.dataUrl.startsWith("data:image/") ? { dataUrl: message.dataUrl } : {}),
        text: typeof message.text === "string" ? message.text.slice(0, 6_000) : "",
        ...(typeof message.error === "string" ? { error: message.error.slice(0, 600) } : {}),
      };
    }
  });

  const rawWait = options.sleep ?? sleep;
  const cancelled = (): Error => new Error("Create playtest was cancelled");
  const checkCancelled = (): void => { if (options.signal?.aborted) throw cancelled(); };
  const wait = async (ms: number): Promise<void> => {
    checkCancelled();
    if (!options.signal) return rawWait(ms);
    const signal = options.signal;
    let onAbort: (() => void) | undefined;
    try {
      await Promise.race([rawWait(ms), new Promise<never>((_, reject) => {
        onAbort = () => reject(cancelled());
        signal.addEventListener("abort", onAbort, { once: true });
      })]);
      checkCancelled();
    } finally { if (onAbort) signal.removeEventListener("abort", onAbort); }
  };
  // Synchronous abort cleanup runs before a preview's iframe is removed.
  const cleanup = (): void => {
    for (const action of active.values()) {
      try { transport.post({ type: "carcade-test-input-v2", action: { ...action, phase: action.type === "pointer" ? "cancel" : "up" } }); } catch { /* Continue releasing other contacts. */ }
    }
    active.clear();
  };
  options.signal?.addEventListener("abort", cleanup);
  try {
    checkCancelled();
    await Promise.race([readySignal, wait(options.readyTimeoutMs ?? 3_000)]);
    transport.post({ type: "carcade-discover-controls-v1" });
    await wait(32);
    let intendedAtMs = 0;
    const explorationStarted = now();
    for (const step of options.actions ?? discoveredActions(controls)) {
      intendedAtMs += step.delayMs;
      await wait(step.delayMs);
      checkCancelled();
      const action = step.action;
      if ("phase" in action) {
        const id = action.type === "pointer" ? `pointer:${action.pointerId}` : `key:${action.code}`;
        if (action.phase === "down" || action.phase === "move") active.set(id, action);
        if (inputActions.length < 32) inputActions.push({intendedAtMs, sentAtMs: now() - explorationStarted, action});
        transport.post({ type: "carcade-test-input-v2", action });
        if (action.phase === "up" || action.phase === "cancel") active.delete(id);
      } else transport.post({ type: "carcade-test-input-v1", action });
      actionsSent += 1;
    }
    cleanup();
    transport.post({ type: "carcade-control-v1", action: "pause" });
    await wait(80);
    transport.post({ type: "carcade-control-v1", action: "resume" });
    transport.post({ type: "carcade-capture-v1", requestId: `playtest-${startedAt}` });
    await wait(options.settleMs ?? 350);
  } finally {
    cleanup();
    options.signal?.removeEventListener("abort", cleanup);
    unsubscribe();
  }

  return {
    version: 1,
    controls, inputReceipts, inputActions,
    ready,
    durationMs: Math.max(0, now() - startedAt),
    actionsSent,
    stateChanges,
    states,
    events,
    errors,
    ...(visual ? { visual } : {}),
  };
}

export function framePlaytestTransport(frame: HTMLIFrameElement, scope: Window = window): PlaytestTransport {
  return {
    subscribe(listener) {
      const handler = (event: MessageEvent): void => {
        if (event.source !== frame.contentWindow) return;
        listener({ data: event.data });
      };
      scope.addEventListener("message", handler);
      return () => scope.removeEventListener("message", handler);
    },
    post(message) {
      frame.contentWindow?.postMessage(message, "*");
    },
  };
}

export function compactPlaytestEvidence(evidence: PlaytestEvidence): Record<string, unknown> {
  return {
    version: evidence.version,
    ready: evidence.ready,
    durationMs: evidence.durationMs,
    actionsSent: evidence.actionsSent,
    controls: evidence.controls ?? [],
    inputReceipts: evidence.inputReceipts ?? [],
    inputActions: evidence.inputActions ?? [],
    stateChanges: evidence.stateChanges,
    states: evidence.states.map((sample) => ({ atMs: sample.atMs, state: sample.state })),
    events: evidence.events,
    errors: evidence.errors,
    visualText: evidence.visual?.text ?? "",
    visualError: evidence.visual?.error ?? null,
    hasVisual: Boolean(evidence.visual?.dataUrl),
  };
}
